import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";
import logoIcon from "@/assets/yieldsocial-icon.png";
import newLogo from "@/assets/yieldsocial-logo-new.png";
import { useEffect, useRef } from "react";

interface SignupSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SignupSuccessModal = ({ isOpen, onClose }: SignupSuccessModalProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen || !dialogRef.current) return;

    const root = dialogRef.current;

    const getPath = (el: Element) => {
      const parts: string[] = [];
      let node: Element | null = el;
      while (node && node.nodeType === 1 && parts.length < 10) {
        const name = node.nodeName.toLowerCase();
        const id = (node as HTMLElement).id ? `#${(node as HTMLElement).id}` : "";
        const cls = (node as HTMLElement).className ? `.${(node as HTMLElement).className.toString().split(/\s+/).join('.')}` : "";
        parts.unshift(`${name}${id}${cls}`);
        node = node.parentElement;
      }
      return parts.join(" > ");
    };

    const purgeIn = (container: ParentNode) => {
      const matchesWelcome = (text: string) => {
        const v = text.toLowerCase().replace(/\s+/g, " ");
        return (
          v.includes("welcome to yieldsocial") ||
          v.includes("welcome to yield social") ||
          v.includes("welcome to yieldsocial!") ||
          v.includes("welcome to yield social!")
        );
      };

      let removed = 0;

      // Remove elements containing the phrase
      const els = (container as Element).querySelectorAll?.("*") as NodeListOf<HTMLElement> | undefined;
      els?.forEach((el) => {
        const t = el.textContent?.trim();
        if (!t) return;
        if (matchesWelcome(t) || t.includes("🌱")) {
          console.warn("SignupSuccessModal: removing element match", getPath(el), (el.outerHTML || "").slice(0, 180));
          el.remove();
          removed++;
        }
      });

      // Remove stray text nodes directly
      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
      const toRemove: ChildNode[] = [];
      let node = walker.nextNode();
      while (node) {
        const text = node.textContent?.trim() || "";
        if (matchesWelcome(text)) {
          toRemove.push(node as ChildNode);
        }
        node = walker.nextNode();
      }
      toRemove.forEach((n) => {
        n.parentNode?.removeChild(n);
        removed++;
      });

      if (removed > 0) console.info("SignupSuccessModal: purged duplicate welcome nodes:", removed);
    };

    // Run immediately and shortly after mount in case of late insertions
    purgeIn(root);
    purgeIn(document.body);
    const t1 = setTimeout(() => { purgeIn(root); purgeIn(document.body); }, 50);
    const t2 = setTimeout(() => { purgeIn(root); purgeIn(document.body); }, 300);

    // Observe future mutations while dialog is open
    const observerRoot = new MutationObserver(() => { purgeIn(root); });
    observerRoot.observe(root, { childList: true, subtree: true, characterData: true });

    const observerBody = new MutationObserver(() => { purgeIn(document.body); });
    observerBody.observe(document.body, { childList: true, subtree: true, characterData: true });

    return () => {
      observerRoot.disconnect();
      observerBody.disconnect();
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent ref={dialogRef} className="sm:max-w-md bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200">
        <div ref={contentRef} className="text-center space-y-4">
          
          <div className="mx-auto flex h-16 w-16 items-center justify-center">
            <img 
              src={newLogo} 
              alt="YieldSocial Logo"
              className="h-16 w-16 object-contain"
            />
          </div>
          
          <h2 className="text-3xl font-bold text-green-800">
            Welcome to YieldSocial
          </h2>
          
          <div className="space-y-4">
            <div className="relative bg-white/80 rounded-lg p-4 border border-green-200 shadow-sm">
              <div className="absolute -top-2 -left-2">
                <Sparkles className="h-4 w-4 text-yellow-500" />
              </div>
              <blockquote className="text-sm text-gray-700 italic leading-relaxed">
                "The spirit of the farmer united with the land, rising in victory and protection, walking the path of transformation to create shared abundance."
              </blockquote>
              <footer className="text-xs text-green-700 font-medium mt-2 text-right">
                - Nancy, Founder
              </footer>
            </div>
            
            <p className="text-sm text-gray-600">
              Your journey to connect with fellow farmers and transform agriculture begins now!
            </p>
          </div>
        </div>
        
        <div className="flex justify-center mt-4">
          <Button 
            onClick={onClose}
            className="bg-green-600 hover:bg-green-700 text-white px-6"
          >
            Continue to YieldSocial
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};